import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthenticationContainer } from 'entities/Authentication';
import { LanguageSwitch } from 'features/SwitchLanguage';
import {
  PlatformToken,
  useExchangeRuOnboardingOtp,
  useOtpExchange,
  useSendOtp,
  useStartRuOnboardingOtp,
} from 'shared/api/otpApi';
import { Flex, MaterialInput, Button, Typography, Envelope } from 'shared/_ui';
import styles from './styles.module.scss';
//import { getRouteReset } from 'shared/consts';
import { notify } from 'shared/lib/notification';
import { useForm } from 'react-hook-form';
import i18next from 'i18next';
import {
  getRouteLogin,
  getRouteMain,
  getRouteRussiaExistingAccount,
  getRouteRussiaLanding,
  getRouteRussiaOnboardingSuccess,
} from 'shared/consts';
import { useUserActions } from 'entities/User';
import { useNavigate } from 'react-router-dom';
import { clearIngressPath, getIngressPath } from 'shared/lib/ingressPath';
import { clearStoredUtmTags, getStoredUtmTags } from 'shared/lib/utmTags';
import { sendAnalyticsEvent, sendMetaPixelEvent } from 'shared/lib/analytics';
import { clearAuthFlow, getAuthFlow } from 'shared/lib/authFlow';
import { getOtpErrorTranslationKey } from '../model/getOtpErrorTranslationKey';
import { shouldOpenRussiaOnboarding } from '../model/shouldOpenRussiaOnboarding';

interface TypingOtp {
  t1: string;
  t2: string;
  t3: string;
  t4: string;
}

const OTP_INPUT_IDS = ['t1', 't2', 't3', 't4'] as const;
type OtpInputIDs = (typeof OTP_INPUT_IDS)[number];

export const OtpCheckPage: React.FC = () => {
  const { t } = useTranslation();
  const [exchange, { isLoading }] = useOtpExchange();
  const [exchangeRuOnboarding, { isLoading: isRuOnboardingExchangeLoading }] =
    useExchangeRuOnboardingOtp();
  const email = localStorage.getItem('user-email');
  const [otplogin] = useSendOtp();
  const [startRuOnboarding] = useStartRuOnboardingOtp();
  const { setAuthData } = useUserActions();
  const navigate = useNavigate();
  const authCompletedRef = useRef(false);

  useEffect(() => {
    if (!email && !authCompletedRef.current) {
      navigate(getRouteLogin(), { replace: true });
    }
  }, [email, navigate]);

  function setFocusTo(id: OtpInputIDs) {
    document.getElementById(id)?.focus();
  }

  const { register, handleSubmit, reset, setValue } = useForm<TypingOtp>({
    mode: 'onSubmit',
    defaultValues: { t1: '', t2: '', t3: '', t4: '' },
  });

  const onSubmit = async (v: TypingOtp) => {
    if (!email) {
      navigate(getRouteLogin(), { replace: true });
      return;
    }

    const otpV = v.t1 + v.t2 + v.t3 + v.t4;
    const authFlow = getAuthFlow();
    const ingressPath = getIngressPath();
    const isRuOnboarding =
      authFlow === 'sign_up' && ingressPath === getRouteRussiaLanding();

    try {
      let tokenResponse: PlatformToken;
      if (isRuOnboarding) {
        const response = await exchangeRuOnboarding({
          email,
          otp: Number(otpV),
        }).unwrap();
        if (response.status === 'ALREADY_REGISTERED') {
          localStorage.removeItem('resend-time');
          localStorage.removeItem('user-email');
          clearAuthFlow();
          clearIngressPath();
          clearStoredUtmTags();
          navigate(getRouteRussiaExistingAccount(), { replace: true });
          return;
        }
        tokenResponse = {
          token: response.token,
          tokenLifetime: response.tokenLifetime,
          redirectUrl: response.redirectUrl,
          setupUrl: response.setupUrl,
          telegramBindUrl: response.telegramBindUrl,
          returningUser: response.returningUser,
        };
      } else {
        tokenResponse = await exchange({ email, otp: Number(otpV) }).unwrap();
      }
      const isRussiaOnboarding = isRuOnboarding || shouldOpenRussiaOnboarding(
        ingressPath,
        authFlow,
        tokenResponse.setupUrl
      );

      authCompletedRef.current = true;

      if (authFlow === 'sign_up' && !tokenResponse.returningUser) {
        sendAnalyticsEvent('sign_up', { method: 'email' });
        sendMetaPixelEvent('CompleteRegistration', {
          content_name: 'VPN Signup',
          status: true,
        });
      }

      if (authFlow === 'login' || tokenResponse.returningUser) {
        sendAnalyticsEvent('login', { method: 'email' });
      }

      const { setupUrl, telegramBindUrl, returningUser, ...authData } = tokenResponse;
      setAuthData(authData);
      localStorage.removeItem('resend-time');
      localStorage.removeItem('user-email');
      clearAuthFlow();
      clearIngressPath();
      clearStoredUtmTags();
      navigate(
        isRussiaOnboarding ? getRouteRussiaOnboardingSuccess() : getRouteMain(),
        {
          replace: true,
          state: isRussiaOnboarding ? { email, setupUrl, telegramBindUrl, returningUser } : undefined,
        }
      );
    } catch (error: unknown) {
      notify.error(t(getOtpErrorTranslationKey(error)));
    }
  };

  const onResend = async () => {
    if (!email) {
      navigate(getRouteLogin(), { replace: true });
      return;
    }

    const now = Date.now();
    const resendTime = Number(localStorage.getItem('resend-time') ?? 0);

    if (resendTime > now) {
      notify.error(
        `${t('authentication.otp.wait')}${Math.ceil(
          (resendTime - now) / 1000
        )}${t('authentication.otp.seconds')}`
      );
      return;
    }

    const request = {
      email,
      languageCode: i18next.language,
      ...getStoredUtmTags(),
    };
    const isRuOnboarding =
      getAuthFlow() === 'sign_up' && getIngressPath() === getRouteRussiaLanding();

    try {
      if (isRuOnboarding) {
        const response = await startRuOnboarding(request).unwrap();
        if (response.status === 'ALREADY_REGISTERED') {
          localStorage.removeItem('resend-time');
          localStorage.removeItem('user-email');
          clearAuthFlow();
          clearIngressPath();
          clearStoredUtmTags();
          navigate(getRouteRussiaExistingAccount(), { replace: true });
          return;
        }
      } else {
        await otplogin(request).unwrap();
      }

      localStorage.setItem('resend-time', (Date.now() + 60000).toString());
      notify.success(t('authentication.otp.resendSuccess'));
    } catch {
      notify.error(t('authentication.login.error'));
    }
    reset({ t1: '', t2: '', t3: '', t4: '' });
  };

  const setOtpInputValue = (id: OtpInputIDs, value: string) => {
    const input = document.getElementById(id) as HTMLInputElement | null;

    if (input) {
      input.value = value;
    }

    setValue(id, value, { shouldDirty: true });
  };

  const handleInput = (id: OtpInputIDs, focusID: OtpInputIDs) => {
    return (event: React.FormEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value.replace(/\D/g, '').slice(-1);
      setOtpInputValue(id, value);

      if (value) {
        setFocusTo(focusID);
      }
    };
  };

  const handlePaste = (id: OtpInputIDs) => {
    return (event: React.ClipboardEvent<HTMLInputElement>) => {
      const digits = event.clipboardData
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, OTP_INPUT_IDS.length);

      if (!digits) {
        return;
      }

      event.preventDefault();

      const startIndex =
        digits.length === OTP_INPUT_IDS.length ? 0 : OTP_INPUT_IDS.indexOf(id);

      OTP_INPUT_IDS.forEach((inputId, index) => {
        const digit = digits[index - startIndex] ?? '';
        setOtpInputValue(inputId, index >= startIndex ? digit : '');
      });

      const nextEmptyIndex = OTP_INPUT_IDS.findIndex(
        (_, index) => index >= startIndex && !digits[index - startIndex]
      );
      const focusIndex =
        nextEmptyIndex === -1
          ? Math.min(startIndex + digits.length - 1, OTP_INPUT_IDS.length - 1)
          : nextEmptyIndex;

      setFocusTo(OTP_INPUT_IDS[focusIndex]);
    };
  };

  return (
    <>
      <AuthenticationContainer
        layoutControls={<LanguageSwitch />}
        title={t('authentication.otp.title')}
        hint={
          <Flex direction="row" spacing={5} className={styles.wrapper}>
            <Typography.Text type="secondary">
              {t('authentication.otp.nocode')}
            </Typography.Text>
            <Typography.Link
              type="primary"
              onClick={onResend}
              analyticsEventName="click_Resend"
            >
              {t('authentication.otp.resend')}
            </Typography.Link>
          </Flex>
        }
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex spacing={20} direction="column" className={styles.wrapper}>
            <Flex
              justify="space-between"
              spacing={10}
              direction="row"
              className={styles.wrapper}
            >
              <MaterialInput
                className={styles.input}
                id="t1"
                onInput={handleInput('t1', 't2')}
                onPaste={handlePaste('t1')}
                onClickCapture={(event) => event.stopPropagation()}
                type="tel"
                maxLength={1}
                {...register('t1')}
              ></MaterialInput>
              <MaterialInput
                className={styles.input}
                id="t2"
                onInput={handleInput('t2', 't3')}
                onPaste={handlePaste('t2')}
                onClickCapture={(event) => event.stopPropagation()}
                {...register('t2')}
                type="tel"
                maxLength={1}
              ></MaterialInput>
              <MaterialInput
                className={styles.input}
                id="t3"
                onInput={handleInput('t3', 't4')}
                onPaste={handlePaste('t3')}
                onClickCapture={(event) => event.stopPropagation()}
                {...register('t3')}
                type="tel"
                maxLength={1}
              ></MaterialInput>
              <MaterialInput
                className={styles.input}
                id="t4"
                onInput={handleInput('t4', 't4')}
                onPaste={handlePaste('t4')}
                onClickCapture={(event) => event.stopPropagation()}
                {...register('t4')}
                type="tel"
                maxLength={1}
              ></MaterialInput>
            </Flex>

            <Typography.Text>{t('authentication.otp.code')}</Typography.Text>

            <Typography.Mark
              className={styles.mail}
              onClickCapture={(event) => event.stopPropagation()}
            >
              <Envelope className={styles.envelope} />
              <Typography.Text strong type="default" size={2}>
                {email}
              </Typography.Text>
            </Typography.Mark>
            <Flex justify="space-between" spacing={10} direction="row">
              <Button
                htmlType="button"
                type="secondary"
                onClick={() => {
                  localStorage.removeItem('user-email');
                  clearAuthFlow();
                  navigate(getIngressPath() || getRouteLogin(), { replace: true });
                }}
                className={styles.buttons}
                analyticsEventName="click_Back"
              >
                {t('shared.text.back')}
              </Button>
              <Button
                htmlType="submit"
                type="primary"
                loading={isLoading || isRuOnboardingExchangeLoading}
                className={styles.buttons}
                analyticsEventName="click_Next"
              >
                {t('authentication.otp.next')}
              </Button>
            </Flex>
          </Flex>
        </form>
      </AuthenticationContainer>
    </>
  );
};
